import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Plus, Printer, Search } from 'lucide-react';
import {
  getCustomer,
  listCustomerDevices,
  listCustomers,
  lookupCustomersByPhone,
  type CustomerDeviceHistoryItem,
  type CustomerListItem,
} from '@/lib/api/customers.js';
import { listDeviceMakes, listDeviceModels, createDeviceMake, createDeviceModel } from '@/lib/api/devices.js';
import { listRepairServices, type RepairService } from '@/lib/api/repair-services.js';
import {
  createTicket,
  updateTicket,
} from '@/lib/api/tickets.js';
import { savePredictiveAlerts } from '@/lib/predictive-alerts-storage.js';
import { getCurrentStore } from '@/lib/api/stores.js';
import { CollectDepositModal } from '@/components/tickets/CollectDepositModal.js';
import { PromisedTimePanel } from '@/components/tickets/PromisedTimePanel.js';
import {
  canManageStoreRole,
  evaluateDepositPreview,
  depositRemaining,
  serviceNeedsParts,
} from '@/lib/deposit.js';
import { createCustomerDisplaySession, getDisplayConnectionStatus } from '@/lib/api/display-staff.js';
import { DISPLAY_STAFF_WS_EVENT } from '@/hooks/useDisplayStaffBridge.js';
import { useAuthStore } from '@/stores/auth.js';
import { isManagerRole } from '@/lib/permissions.js';
import { applyTicketTemplate, listTicketTemplates, type TicketTemplate } from '@/lib/api/ticket-templates.js';
import { formatCurrency } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastInfo, toastSuccess } from '@/lib/toast.js';
import { printLabelAfterCreate } from '@/lib/print.js';
import { usePrintNodeConfigured } from '@/hooks/usePrintNodeConfigured.js';
import { useManagerOverride } from '@/hooks/useManagerOverride.js';
import { ManagerOverrideModal } from '@/components/common/ManagerOverrideModal.js';
import { usePermissions } from '@/hooks/usePermissions.js';
import {
  CustomerPreviousDevicesPanel,
} from '@/components/tickets/CustomerPreviousDevicesPanel.js';
import { SnippetTextarea } from '@/components/common/SnippetTextarea.js';

const preferredMakes = ['Apple', 'Samsung', 'Google', 'Motorola', 'Other'];
const conditions = [
  ['excellent', 'bg-green-500'],
  ['good', 'bg-emerald-500'],
  ['fair', 'bg-amber-500'],
  ['poor', 'bg-orange-500'],
  ['broken', 'bg-red-500'],
] as const;
const passcodeTypes = ['pin', 'password', 'face_id', 'fingerprint', 'none', 'unknown'];

interface QuickDevice {
  makeId: string;
  makeName: string;
  modelId: string;
  modelName: string;
  modelSearch: string;
  condition: string;
  passcodeType: string;
  passcode: string;
  imei: string;
  color: string;
  serialNumber: string;
  fromHistory: boolean;
}

function emptyDevice(): QuickDevice {
  return {
    makeId: '',
    makeName: '',
    modelId: '',
    modelName: '',
    modelSearch: '',
    condition: '',
    passcodeType: 'unknown',
    passcode: '',
    imei: '',
    color: '',
    serialNumber: '',
    fromHistory: false,
  };
}

function quickDeviceExcludeKey(device: QuickDevice): string {
  return [
    device.makeId || 'null',
    device.modelId || 'null',
    (device.modelId ? '' : device.modelSearch || '').trim().toLowerCase(),
  ].join('|');
}

function servicePrice(service: RepairService) {
  return String(service.resolvedPrice ?? service.defaultPrice ?? '0');
}

function isUuid(value: string | undefined): value is string {
  return Boolean(
    value?.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i),
  );
}

function templateTileTint(makeName: string | null | undefined): string {
  const n = (makeName ?? '').toLowerCase();
  if (n.includes('apple')) return 'border-sky-200 bg-sky-50/90';
  if (n.includes('samsung')) return 'border-slate-600/25 bg-slate-900/[0.06]';
  if (n.includes('google')) return 'border-emerald-300/60 bg-gradient-to-br from-emerald-50/90 to-cyan-50/80';
  return 'border-slate-200 bg-slate-50/80';
}

function templateListTotal(template: TicketTemplate): number {
  return template.repairs.reduce((sum, r) => sum + Number(r.quotedPrice ?? r.price ?? 0), 0);
}

type PosQuickTicketPrefill = {
  makeId?: string;
  makeName?: string;
  modelId?: string;
  modelName?: string;
  imei?: string;
  color?: string;
  serialNumber?: string;
  passcodeType?: string;
  fromHistory?: boolean;
  repairs?: Array<{ serviceId?: string; serviceName: string; quotedPrice: number }>;
  customerId?: string;
  fromPos?: boolean;
};

export function QuickTicketPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [fromPos, setFromPos] = useState(false);
  const [devices, setDevices] = useState<QuickDevice[]>([
    { ...emptyDevice(), makeId: params.get('makeId') ?? '', modelId: params.get('modelId') ?? '' },
  ]);
  const [activeDevice, setActiveDevice] = useState(0);
  const [repairs, setRepairs] = useState<Record<number, Record<string, { service: RepairService; price: string }>>>({});
  const [customRepairName, setCustomRepairName] = useState('');
  const [customRepairPrice, setCustomRepairPrice] = useState('0');
  const [customRepairMinutes, setCustomRepairMinutes] = useState('60');
  const [editingCustomId, setEditingCustomId] = useState<string | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerListItem | null>(null);
  const [anonymous, setAnonymous] = useState(false);
  const [newCustomerOpen, setNewCustomerOpen] = useState(false);
  const [newFirstName, setNewFirstName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [showManualDevice, setShowManualDevice] = useState(false);
  const [historyDisplayName, setHistoryDisplayName] = useState<string | null>(null);
  const [priority, setPriority] = useState<'normal' | 'rush' | 'urgent'>('normal');
  const [customerComplaint, setCustomerComplaint] = useState('');
  const [intakeNotes, setIntakeNotes] = useState('');
  const [promisedDate, setPromisedDate] = useState(() => {
    const due = new Date(Date.now() + 90 * 60 * 1000);
    const offsetMs = due.getTimezoneOffset() * 60 * 1000;
    return new Date(due.getTime() - offsetMs).toISOString().slice(0, 16);
  });
  const [templateApplied, setTemplateApplied] = useState(false);
  const [appliedTemplateName, setAppliedTemplateName] = useState<string | null>(null);
  const [waitingDisplayCustomer, setWaitingDisplayCustomer] = useState(false);
  const [sendingDisplayCustomer, setSendingDisplayCustomer] = useState(false);
  const storeId = useAuthStore((s) => s.user?.storeId);
  const user = useAuthStore((s) => s.user);
  const canQuickAddDevice = isManagerRole(user?.role ?? '');
  const [makeSearch, setMakeSearch] = useState('');
  const { flags } = usePermissions();
  const { requireOverride, modalProps: managerOverrideModal } = useManagerOverride();
  const canWaiveDeposit =
    Boolean(flags.can_waive_deposit) || canManageStoreRole(user?.role);
  const storeQuery = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore });
  const { hasPrintNode: hasLabelPrinter } = usePrintNodeConfigured('label');
  const [collectDepositChoice, setCollectDepositChoice] = useState<'skip' | 'collect'>('skip');
  const [depositAmountInput, setDepositAmountInput] = useState('');
  const [managerSkipDeposit, setManagerSkipDeposit] = useState(false);
  const [depositModalTicket, setDepositModalTicket] = useState<{
    id: string;
    ticketNumber: string;
    maxAmount: number;
    mandatoryIntake: boolean;
  } | null>(null);
  const [pendingAfterDeposit, setPendingAfterDeposit] = useState<{
    ticketId: string;
    print: boolean;
  } | null>(null);

  useEffect(() => {
    const st = location.state as { prefill?: PosQuickTicketPrefill } | undefined;
    const pf = st?.prefill;
    if (!pf) return;

    const hasDevice = Boolean(pf.makeId || pf.modelId || pf.modelName || pf.fromHistory);
    if (hasDevice) {
      setDevices([
        {
          ...emptyDevice(),
          makeId: pf.makeId ?? '',
          makeName: pf.makeName ?? '',
          modelId: pf.modelId ?? '',
          modelName: pf.modelName ?? '',
          modelSearch: pf.modelName ?? '',
          imei: pf.imei ?? '',
          color: pf.color ?? '',
          serialNumber: pf.serialNumber ?? '',
          passcodeType: pf.passcodeType ?? 'unknown',
          fromHistory: Boolean(pf.fromHistory),
        },
      ]);
      if (pf.fromHistory) {
        setHistoryDisplayName(
          [pf.makeName, pf.modelName].filter(Boolean).join(' ') || pf.modelName || 'Device',
        );
        setShowManualDevice(false);
      } else {
        setShowManualDevice(true);
      }
    }

    if (pf.repairs?.length) {
      const prefilled = Object.fromEntries(
        pf.repairs.map((repair, index) => {
          const service = {
            id: repair.serviceId ?? `pos-prefill-${index}`,
            name: repair.serviceName,
            defaultPrice: String(repair.quotedPrice),
          } as RepairService;
          return [service.id, { service, price: String(repair.quotedPrice) }];
        }),
      );
      setRepairs({ 0: prefilled });
    }

    if (pf.customerId) {
      void getCustomer(pf.customerId).then((row) => {
        setSelectedCustomer(row);
        setAnonymous(false);
      });
    }

    if (pf.fromPos) setFromPos(true);

    // Steps: 0 Templates, 1 Customer, 2 Device, 3 Repairs, 4 Confirm
    if (pf.customerId && hasDevice) setStep(3);
    else if (pf.customerId) setStep(2);
    else if (hasDevice) setStep(2);
    else setStep(1);

    navigate(location.pathname + location.search, { replace: true, state: {} });
  }, [location.state, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!waitingDisplayCustomer) return;
    const handler = (ev: Event) => {
      const msg = (ev as CustomEvent<{ type?: string; payload?: Record<string, string> }>).detail;
      if (msg?.type !== 'display.customer_info_submitted') return;
      const p = msg.payload ?? {};
      const full = [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
      if (full) setNewFirstName(full);
      if (p.phone) setNewPhone(p.phone);
      if (p.email) setNewEmail(p.email);
      setSelectedCustomer(null);
      setAnonymous(false);
      setWaitingDisplayCustomer(false);
      toastSuccess('Customer info received — fields populated ✓');
    };
    window.addEventListener(DISPLAY_STAFF_WS_EVENT, handler as EventListener);
    return () => window.removeEventListener(DISPLAY_STAFF_WS_EVENT, handler as EventListener);
  }, [waitingDisplayCustomer]);

  async function sendCustomerToDisplay() {
    if (!storeId) {
      toastError('No store selected.');
      return;
    }
    setSendingDisplayCustomer(true);
    try {
      const status = await getDisplayConnectionStatus(storeId);
      if (!status.connected) {
        toastError('Customer display not connected. Open the display URL on the facing iPad.');
        return;
      }
      await createCustomerDisplaySession();
      setWaitingDisplayCustomer(true);
      toastSuccess('Waiting for customer to enter info on display…');
    } catch (err) {
      const parsed = parseApiError(err);
      toastError(parsed.message, parsed.code);
    } finally {
      setSendingDisplayCustomer(false);
    }
  }

  const templatesQuery = useQuery({ queryKey: ['ticket-templates'], queryFn: () => listTicketTemplates() });
  const makesQuery = useQuery({ queryKey: ['device-makes'], queryFn: () => listDeviceMakes() });
  const currentDevice = devices[activeDevice]!;
  const modelsQuery = useQuery({
    queryKey: ['device-models', currentDevice.makeId, currentDevice.modelSearch],
    queryFn: () => {
      const query: { makeId?: string; search?: string } = {};
      if (currentDevice.makeId) query.makeId = currentDevice.makeId;
      if (currentDevice.modelSearch.trim()) query.search = currentDevice.modelSearch.trim();
      return listDeviceModels(query);
    },
    enabled: Boolean(currentDevice.makeId),
  });
  const existingCustomerQuery = useQuery({
    queryKey: ['customer', params.get('customerId')],
    queryFn: () => getCustomer(params.get('customerId')!),
    enabled: Boolean(params.get('customerId')) && !selectedCustomer,
  });

  useEffect(() => {
    const cid = params.get('customerId');
    if (!cid) return;
    if (selectedCustomer?.id === cid) return;
    if (!existingCustomerQuery.data) return;
    setSelectedCustomer(existingCustomerQuery.data);
    setAnonymous(false);
    const hasDevice = Boolean(params.get('makeId') || params.get('modelId'));
    setStep(hasDevice ? 3 : 2);
    if (hasDevice) setShowManualDevice(true);
  }, [existingCustomerQuery.data, params, selectedCustomer?.id]);

  const linkedCustomerId = selectedCustomer?.id ?? existingCustomerQuery.data?.id ?? null;

  // Prefetch previous devices as soon as customer is known
  const customerDevicesQuery = useQuery({
    queryKey: ['customer-devices', linkedCustomerId],
    queryFn: () => listCustomerDevices(linkedCustomerId!),
    enabled: Boolean(linkedCustomerId),
    staleTime: 60_000,
    retry: false,
  });

  const phoneSearchDigits = customerSearch.replace(/\D/g, '');
  const isPhoneSearch =
    phoneSearchDigits.length >= 7 &&
    phoneSearchDigits.length / Math.max(customerSearch.replace(/\s/g, '').length, 1) >= 0.7;

  const customersQuery = useQuery({
    queryKey: ['customers', customerSearch],
    queryFn: () => listCustomers({ q: customerSearch, page: 1, pageSize: 8 }),
    enabled: customerSearch.trim().length >= 2 && !isPhoneSearch,
  });

  const phonePeersQuery = useQuery({
    queryKey: ['customers-by-phone', phoneSearchDigits],
    queryFn: () => lookupCustomersByPhone(phoneSearchDigits),
    enabled: isPhoneSearch,
  });
  const phonePeerResults = phonePeersQuery.data ?? [];
  const showPhonePicker = isPhoneSearch && phonePeerResults.length >= 1;

  const servicesQuery = useQuery({
    queryKey: ['repair-services', currentDevice.makeId, currentDevice.modelId],
    queryFn: () =>
      listRepairServices({
        makeId: currentDevice.makeId ?? undefined,
        modelId: currentDevice.modelId ?? undefined,
        displayOnPos: true,
        requireModelPricing: Boolean(currentDevice.modelId),
      }),
    enabled: Boolean(currentDevice.modelId),
  });

  const selectedRepairs = useMemo(
    () =>
      Object.entries(repairs).flatMap(([deviceIndex, selections]) =>
        Object.values(selections).map(({ service, price }) => ({
          deviceIndex: Number(deviceIndex),
          serviceId: isUuid(service.id) ? service.id : undefined,
          serviceName: service.name,
          quotedPrice: Number(price || 0),
          warrantyDays: service.resolvedWarrantyDays ?? service.defaultWarrantyDays ?? undefined,
          warrantyType: service.resolvedWarrantyType ?? service.defaultWarrantyType ?? undefined,
        })),
      ),
    [repairs],
  );
  const total = selectedRepairs.reduce((sum, repair) => sum + repair.quotedPrice, 0);

  const repairsNeedParts = useMemo(
    () =>
      Object.values(repairs).some((deviceRepairs) =>
        Object.values(deviceRepairs).some(({ service }) => serviceNeedsParts(service)),
      ),
    [repairs],
  );

  const depositPreview = useMemo(() => {
    if (!storeQuery.data) return null;
    return evaluateDepositPreview(storeQuery.data, total, { repairsNeedParts });
  }, [storeQuery.data, total, repairsNeedParts]);

  useEffect(() => {
    if (depositPreview && depositPreview.depositAmount > 0) {
      setDepositAmountInput(String(depositPreview.depositAmount));
    }
  }, [depositPreview?.depositAmount]);

  const createMutation = useMutation({
    mutationFn: () => {
      const linkedCustomerId = selectedCustomer?.id ?? existingCustomerQuery.data?.id;
      const newCustomerName = newFirstName.trim();
      const useNewCustomer =
        !anonymous && !linkedCustomerId && Boolean(newCustomerName);

      const mode = storeQuery.data?.depositMode;
      let optionalBodyDeposit: number | undefined;
      if (mode === 'optional' && collectDepositChoice === 'collect' && Number(depositAmountInput) > 0) {
        optionalBodyDeposit = Number(depositAmountInput);
      }

      const payload = {
        intakeType: 'drop_off',
        source: 'walk_in',
        priority,
        customerId: linkedCustomerId,
        customerNameOverride: anonymous
          ? 'Walk-in'
          : linkedCustomerId
            ? undefined
            : useNewCustomer
              ? newCustomerName
              : undefined,
        customerPhoneOverride: useNewCustomer ? newPhone.trim() || undefined : undefined,
        customerEmailOverride: useNewCustomer ? newEmail.trim() || undefined : undefined,
        promisedDate: new Date(promisedDate).toISOString(),
        devices: devices.map((device) => ({
          makeId: device.makeId || undefined,
          modelId: device.modelId || undefined,
          customModelName: device.modelId
            ? undefined
            : [device.makeName, device.modelSearch].filter(Boolean).join(' ') || undefined,
          conditionAtIntake: device.condition || 'good',
          passcodeType: device.passcodeType,
          passcode: ['pin', 'password', 'fingerprint'].includes(device.passcodeType)
            ? device.passcode || undefined
            : undefined,
          accessoriesReceived: [],
          imei: device.imei?.replace(/\D/g, '').trim() || undefined,
          color: device.color.trim() || undefined,
          serialNumber: device.serialNumber.trim() || undefined,
        })),
        repairs: selectedRepairs,
        tags: [],
        customerComplaint: customerComplaint.trim().slice(0, 500) || undefined,
        intakeNotes: intakeNotes.trim() || undefined,
        ...(optionalBodyDeposit != null ? { depositAmount: optionalBodyDeposit } : {}),
      };
      return createTicket(payload);
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const applyTemplateMutation = useMutation({
    mutationFn: (templateId: string) => applyTicketTemplate(templateId),
    onSuccess: (template) => {
      void queryClient.invalidateQueries({ queryKey: ['ticket-templates'] });
      setDevices([
        {
          ...emptyDevice(),
          makeId: template.makeId ?? '',
          modelId: template.modelId ?? '',
          makeName: template.makeName ?? '',
          modelName: template.modelName ?? '',
          modelSearch: template.modelName ?? '',
          condition: template.defaultCondition ?? 'good',
        },
      ]);
      const templateRepairs = Object.fromEntries(
        template.repairs.map((repair, index) => {
          const service = {
            id: repair.serviceId ?? `template-${template.id}-${index}`,
            name: repair.serviceName,
            defaultPrice: String(repair.quotedPrice),
            defaultWarrantyDays: repair.warrantyDays ?? null,
          } as RepairService;
          return [service.id, { service, price: String(repair.quotedPrice) }];
        }),
      );
      setRepairs({ 0: templateRepairs });
      setTemplateApplied(true);
      setAppliedTemplateName(template.name);
      setStep(1);
      toastSuccess(`Using template: ${template.name}`);
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  function updateDevice(index: number, patch: Partial<QuickDevice>) {
    setDevices((current) => current.map((device, i) => (i === index ? { ...device, ...patch } : device)));
  }

  function toggleService(service: RepairService) {
    setRepairs((current) => {
      const next = { ...(current[activeDevice] ?? {}) };
      if (next[service.id]) delete next[service.id];
      else next[service.id] = { service, price: servicePrice(service) };
      return { ...current, [activeDevice]: next };
    });
  }

  function finishAfterCreate(ticketId: string, print: boolean) {
    if (print) {
      void printLabelAfterCreate(ticketId, hasLabelPrinter).then(
        () => {
          toastSuccess('Ticket created — label printing...');
          navigate(`/tickets/${ticketId}`);
        },
        (error: unknown) => {
          const p = parseApiError(error);
          toastError(p.message, p.code);
          navigate(`/tickets/${ticketId}`);
        },
      );
      return;
    }
    navigate(`/tickets/${ticketId}`);
  }

  function clearAppliedTemplate() {
    setTemplateApplied(false);
    setAppliedTemplateName(null);
    setDevices([emptyDevice()]);
    setRepairs({});
    setShowManualDevice(false);
    setHistoryDisplayName(null);
    setStep(0);
  }

  function applyHistoryDevice(device: CustomerDeviceHistoryItem) {
    updateDevice(activeDevice, {
      makeId: device.makeId ?? '',
      makeName: device.makeName ?? '',
      modelId: device.modelId ?? '',
      modelName: device.modelName ?? device.customModelName ?? '',
      modelSearch: device.modelName ?? device.customModelName ?? '',
      imei: device.lastImei ?? '',
      color: device.lastColor ?? '',
      serialNumber: device.lastSerial ?? '',
      passcodeType: device.lastPasscodeType ?? 'unknown',
      condition: '',
      fromHistory: true,
    });
    setHistoryDisplayName(device.displayName);
    setShowManualDevice(false);
  }

  function clearHistorySelection() {
    updateDevice(activeDevice, emptyDevice());
    setHistoryDisplayName(null);
    setShowManualDevice(false);
  }

  function startManualDevice() {
    updateDevice(activeDevice, emptyDevice());
    setHistoryDisplayName(null);
    setShowManualDevice(true);
  }

  function offerSaveTemplateAfterCreate() {
    if (templateApplied || selectedRepairs.length === 0) return;
    const d0 = devices[0];
    const prefill = {
      name: `${d0?.modelName || d0?.modelSearch || 'Device'} — ${selectedRepairs.map((r) => r.serviceName).join(' + ')}`,
      makeId: d0?.makeId || null,
      modelId: d0?.modelId || null,
      defaultCondition: d0?.condition ?? 'good',
      repairs: selectedRepairs.map((r) => ({
        serviceId: r.serviceId,
        serviceName: r.serviceName,
        quotedPrice: r.quotedPrice,
        warrantyDays: r.warrantyDays,
      })),
      tags: ['quick-ticket'],
    };
    toast.custom(
      (t) => (
        <div className="flex max-w-md flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-lg">
          <p className="font-semibold text-slate-950">Ticket created!</p>
          <p className="text-sm text-slate-600">Save as template for next time?</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700"
              onClick={() => toast.dismiss(t.id)}
            >
              Dismiss
            </button>
            <button
              type="button"
              className="rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white"
              onClick={() => {
                toast.dismiss(t.id);
                navigate('/settings/templates', { state: { templatePrefill: prefill } });
              }}
            >
              Save Template
            </button>
          </div>
        </div>
      ),
      { duration: 12_000 },
    );
  }

  function createNow(print: boolean) {
    if (storeQuery.data?.depositMode === 'optional' && collectDepositChoice === 'collect') {
      const amt = Number(depositAmountInput);
      if (!Number.isFinite(amt) || amt <= 0) {
        toastError('Enter a deposit amount before creating the ticket.');
        return;
      }
    }

    createMutation.mutate(undefined, {
      onSuccess: async (ticket) => {
        if (ticket.predictiveAlerts?.length) {
          savePredictiveAlerts(ticket.ticket.id, ticket.predictiveAlerts);
        }
        const remaining = depositRemaining(
          ticket.ticket.depositAmount,
          ticket.ticket.depositCollected,
        );

        if (ticket.ticket.depositRequired && managerSkipDeposit && remaining > 0) {
          const result = await requireOverride({
            action: 'waive_deposit',
            title: 'Manager Approval to Waive Deposit',
            description: `Waiving required deposit of ${formatCurrency(remaining)}`,
            context: { ticketId: ticket.ticket.id, amount: remaining },
          });
          if (!result.approved) {
            setDepositModalTicket({
              id: ticket.ticket.id,
              ticketNumber: ticket.ticket.ticketNumber,
              maxAmount: remaining,
              mandatoryIntake: true,
            });
            setPendingAfterDeposit({ ticketId: ticket.ticket.id, print });
            return;
          }
          await updateTicket(ticket.ticket.id, {
            depositSkipped: true,
            managerOverrideAuthorizedBy: result.authorizedBy.id,
          });
          toastInfo('Deposit skipped — manager override');
          offerSaveTemplateAfterCreate();
          finishAfterCreate(ticket.ticket.id, print);
          return;
        }

        const requiresMandatoryModal = Boolean(
          ticket.ticket.depositRequired && remaining > 0,
        );
        const optionalCollectModal =
          !requiresMandatoryModal &&
          remaining > 0 &&
          storeQuery.data?.depositMode === 'optional' &&
          collectDepositChoice === 'collect';

        if (requiresMandatoryModal || optionalCollectModal) {
          setPendingAfterDeposit({ ticketId: ticket.ticket.id, print });
          setDepositModalTicket({
            id: ticket.ticket.id,
            ticketNumber: ticket.ticket.ticketNumber,
            maxAmount: remaining,
            mandatoryIntake: requiresMandatoryModal,
          });
          return;
        }

        toastSuccess(`Ticket ${ticket.ticket.ticketNumber} created successfully`);
        offerSaveTemplateAfterCreate();
        finishAfterCreate(ticket.ticket.id, print);
      },
      onError: (error) => {
        const parsed = parseApiError(error);
        toastError(parsed.message, parsed.code);
      },
    });
  }

  const models = modelsQuery.data ?? [];

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      {fromPos ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-950">
          <span className="font-semibold">Creating ticket from POS</span>
          <Link
            to="/pos"
            className="rounded-lg border border-teal-300 bg-white px-3 py-1.5 text-xs font-bold text-teal-800 hover:bg-teal-50"
          >
            ← Back to POS
          </Link>
        </div>
      ) : null}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-6 flex gap-3">
          {[0, 1, 2, 3, 4].map((n) => (
            <div key={n} className={`h-2 flex-1 rounded-full ${step >= n ? 'bg-primary' : 'bg-slate-200'}`} />
          ))}
        </div>

        {templateApplied && Boolean(appliedTemplateName) && step > 0 ? (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-primary/30 bg-primary/5 px-4 py-2 text-sm text-slate-800">
            <span>
              Using template: <strong>{appliedTemplateName}</strong>
            </span>
            <button
              type="button"
              className="rounded-lg border border-slate-300 bg-white px-3 py-1 text-xs font-semibold"
              onClick={clearAppliedTemplate}
            >
              × Change
            </button>
          </div>
        ) : null}

        {step === 0 ? (
          <section className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-xl font-semibold text-slate-950">Templates</h2>
              <button className="rounded-xl bg-primary px-4 py-2 font-semibold text-white" onClick={() => setStep(1)}>
                + New Ticket
              </button>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {templatesQuery.data?.map((template) => {
                const total = templateListTotal(template);
                const deviceLabel = template.modelName || template.makeName || 'Device';
                return (
                  <button
                    key={template.id}
                    type="button"
                    className={[
                      'min-w-[10.5rem] shrink-0 rounded-2xl border p-4 text-left shadow-sm transition',
                      templateTileTint(template.makeName),
                      applyTemplateMutation.isPending ? 'opacity-60' : 'hover:-translate-y-0.5 hover:shadow-md',
                    ].join(' ')}
                    disabled={applyTemplateMutation.isPending}
                    onClick={() => applyTemplateMutation.mutate(template.id)}
                  >
                    <div className="text-2xl" aria-hidden>
                      📱
                    </div>
                    <div className="mt-2 line-clamp-1 font-bold text-slate-950">{deviceLabel}</div>
                    <div className="mt-1 line-clamp-2 text-xs text-slate-600">{template.name}</div>
                    <div className="mt-2 text-sm font-bold text-slate-900">{formatCurrency(total)}</div>
                    <div className="mt-1 text-xs font-semibold text-amber-700">
                      ⚡ Used {template.usageCount}×
                    </div>
                  </button>
                );
              })}
              {templatesQuery.data?.length === 0 ? <div className="text-sm text-slate-500">No templates yet.</div> : null}
            </div>
          </section>
        ) : null}

        {step === 1 ? (
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-950">Step 1 - Customer</h2>
            <button
              type="button"
              disabled={sendingDisplayCustomer || Boolean(selectedCustomer) || anonymous}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-primary/40 bg-primary/5 px-4 py-3 text-sm font-semibold text-primary hover:bg-primary/10 disabled:opacity-50 sm:w-auto"
              onClick={() => void sendCustomerToDisplay()}
            >
              📺 Customer enters info on display
            </button>
            {waitingDisplayCustomer ? (
              <p className="flex items-center gap-2 text-sm text-slate-600">
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                Waiting for customer on display…
              </p>
            ) : null}
            <input className="w-full rounded-xl border border-slate-300 px-3 py-2" placeholder="Search phone or name" value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} />
            {showPhonePicker ? (
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-3">
                <p className="text-sm font-semibold text-slate-900">
                  {phonePeerResults.length} customer{phonePeerResults.length === 1 ? '' : 's'} with
                  this number
                </p>
                <div className="mt-2 space-y-1">
                  {phonePeerResults.map((customer) => (
                    <button
                      key={customer.id}
                      type="button"
                      className="flex w-full justify-between rounded-xl border border-slate-200 bg-white p-3 text-left"
                      onClick={() => {
                        setSelectedCustomer({
                          id: customer.id,
                          firstName: customer.firstName,
                          lastName: customer.lastName ?? '',
                          email: customer.email,
                          phone: customer.phone,
                          totalTickets: customer.totalTickets,
                          totalSpend: '0',
                          lastVisitAt: customer.lastVisitAt,
                          customerStatus: customer.customerStatus,
                        });
                        setAnonymous(false);
                        setShowManualDevice(false);
                        setHistoryDisplayName(null);
                        setDevices([emptyDevice()]);
                      }}
                    >
                      <span>
                        <span className="font-semibold">
                          {[customer.firstName, customer.lastName].filter(Boolean).join(' ')}
                        </span>
                        <span className="mt-0.5 block text-xs text-slate-500">
                          {customer.totalTickets} tickets
                          {customer.lastVisitAt
                            ? ` · last ${new Date(customer.lastVisitAt).toLocaleDateString()}`
                            : ''}
                        </span>
                      </span>
                      {selectedCustomer?.id === customer.id ? <Check className="h-4 w-4 text-primary" /> : null}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="w-full rounded-xl border border-dashed border-slate-300 px-3 py-2 text-left text-sm font-semibold"
                    onClick={() => {
                      setSelectedCustomer(null);
                      setNewPhone(customerSearch);
                      setNewCustomerOpen(true);
                      setCustomerSearch('');
                    }}
                  >
                    + New customer with this number
                  </button>
                </div>
              </div>
            ) : null}
            {!showPhonePicker
              ? customersQuery.data?.data.map((customer) => (
              <button key={customer.id} type="button" className="flex w-full justify-between rounded-xl border border-slate-200 p-3 text-left" onClick={() => {
                setSelectedCustomer(customer);
                setAnonymous(false);
                setShowManualDevice(false);
                setHistoryDisplayName(null);
                setDevices([emptyDevice()]);
              }}>
                <span>{[customer.firstName, customer.lastName].filter(Boolean).join(' ')}</span>
                {selectedCustomer?.id === customer.id ? <Check className="h-4 w-4 text-primary" /> : null}
              </button>
            ))
              : null}
            <button type="button" className="rounded-xl border border-slate-300 px-4 py-2 font-semibold" onClick={() => setNewCustomerOpen((v) => !v)}>+ New Customer</button>
            {newCustomerOpen ? (
              <div className="grid gap-3 rounded-2xl bg-slate-50 p-4 sm:grid-cols-2">
                <input className="rounded-xl border border-slate-300 px-3 py-2" placeholder="Name" value={newFirstName} onChange={(e) => setNewFirstName(e.target.value)} />
                <input className="rounded-xl border border-slate-300 px-3 py-2" placeholder="Phone" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} />
                <input className="col-span-full rounded-xl border border-slate-300 px-3 py-2" placeholder="Email (optional)" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
                <p className="col-span-full text-xs text-slate-500">
                  Customer is saved automatically when you create the ticket.
                </p>
              </div>
            ) : null}
            <button type="button" className={`rounded-xl px-4 py-2 font-semibold ${anonymous ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700'}`} onClick={() => { setAnonymous(true); setSelectedCustomer(null); }}>
              Walk-in / Anonymous
            </button>
          </section>
        ) : null}

        {step === 2 ? (
          <section className="space-y-5">
            <h2 className="text-xl font-semibold text-slate-950">Step 2 - Device</h2>
            {linkedCustomerId && !showManualDevice && !currentDevice.fromHistory ? (
              <CustomerPreviousDevicesPanel
                customerId={linkedCustomerId}
                customerFirstName={
                  selectedCustomer?.firstName ?? existingCustomerQuery.data?.firstName ?? null
                }
                excludeKeys={devices
                  .map((d, i) => (i === activeDevice ? null : quickDeviceExcludeKey(d)))
                  .filter((k): k is string => Boolean(k) && k !== 'null|null|')}
                onSelect={applyHistoryDevice}
                onNewDevice={startManualDevice}
              />
            ) : null}
            {currentDevice.fromHistory && historyDisplayName ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950">
                <div>
                  <div className="font-semibold">✓ Using {historyDisplayName}</div>
                  <div className="text-emerald-800/80">
                    Details pre-filled from last visit. Please verify and update as needed.
                  </div>
                </div>
                <button
                  type="button"
                  className="rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold"
                  onClick={clearHistorySelection}
                >
                  Change →
                </button>
              </div>
            ) : null}
            {(
              !linkedCustomerId ||
              showManualDevice ||
              currentDevice.fromHistory ||
              customerDevicesQuery.isError ||
              (customerDevicesQuery.isSuccess && (customerDevicesQuery.data?.length ?? 0) === 0)
            ) ? (
              <>
                {devices.map((device, index) => (
                  <div key={index} className="rounded-2xl border border-slate-200 p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <div className="font-semibold">Device {index + 1}</div>
                      <div className="flex gap-3">
                        <button type="button" onClick={() => setActiveDevice(index)} className="text-sm font-semibold text-primary">
                          Edit
                        </button>
                        {devices.length > 1 ? (
                          <button
                            type="button"
                            className="text-sm font-semibold text-red-600"
                            onClick={() => {
                              setDevices((current) => current.filter((_, i) => i !== index));
                              setRepairs((current) => {
                                const next: typeof current = {};
                                let newIdx = 0;
                                for (let i = 0; i < devices.length; i++) {
                                  if (i === index) continue;
                                  if (current[i]) next[newIdx] = current[i]!;
                                  newIdx += 1;
                                }
                                return next;
                              });
                              setActiveDevice((cur) => (cur >= index ? Math.max(0, cur - 1) : cur));
                            }}
                          >
                            Remove
                          </button>
                        ) : null}
                      </div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-5">
                      {preferredMakes.map((name) => {
                        const make = makesQuery.data?.find((m) => m.name === name);
                        const selected = device.makeId && device.makeId === make?.id;
                        return (
                          <button
                            key={name}
                            type="button"
                            onClick={() => {
                              setActiveDevice(index);
                              if (!make && canQuickAddDevice && name !== 'Other') {
                                void (async () => {
                                  try {
                                    const created = await createDeviceMake({
                                      name,
                                      category: 'phone',
                                    });
                                    await queryClient.invalidateQueries({ queryKey: ['device-makes'] });
                                    updateDevice(index, {
                                      makeId: created.id,
                                      makeName: created.name,
                                      modelId: '',
                                      modelName: '',
                                      modelSearch: '',
                                    });
                                    toastSuccess(`${created.name} added to catalog`);
                                  } catch (err) {
                                    toastError(parseApiError(err).message);
                                  }
                                })();
                                return;
                              }
                              updateDevice(index, {
                                makeId: make?.id ?? '',
                                makeName: name,
                                modelId: '',
                                modelName: '',
                                modelSearch: '',
                              });
                            }}
                            className={`rounded-2xl border p-4 text-center font-semibold ${selected ? 'border-primary bg-primary/5 text-primary' : 'border-slate-200'}`}
                          >
                            {name}
                          </button>
                        );
                      })}
                    </div>
                    <div className="relative mt-3">
                      <input
                        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                        placeholder="Or search / add manufacturer..."
                        value={
                          device.makeId
                            ? device.makeName
                            : makeSearch && activeDevice === index
                              ? makeSearch
                              : device.makeName
                        }
                        onFocus={() => setActiveDevice(index)}
                        onChange={(e) => {
                          setActiveDevice(index);
                          setMakeSearch(e.target.value);
                          updateDevice(index, {
                            makeId: '',
                            makeName: e.target.value,
                            modelId: '',
                            modelName: '',
                            modelSearch: '',
                          });
                        }}
                      />
                      {activeDevice === index && makeSearch.trim() && !device.makeId ? (
                        <div className="absolute z-20 mt-2 max-h-48 w-full overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg">
                          {(makesQuery.data ?? [])
                            .filter((m) =>
                              m.name.toLowerCase().includes(makeSearch.trim().toLowerCase()),
                            )
                            .slice(0, 8)
                            .map((make) => (
                              <button
                                key={make.id}
                                type="button"
                                className="block w-full px-4 py-2.5 text-left text-sm hover:bg-slate-50"
                                onClick={() => {
                                  updateDevice(index, {
                                    makeId: make.id,
                                    makeName: make.name,
                                    modelId: '',
                                    modelName: '',
                                    modelSearch: '',
                                  });
                                  setMakeSearch('');
                                }}
                              >
                                {make.name}
                              </button>
                            ))}
                          {(makesQuery.data ?? []).every(
                            (m) => m.name.toLowerCase() !== makeSearch.trim().toLowerCase(),
                          ) ? (
                            canQuickAddDevice ? (
                              <button
                                type="button"
                                className="block w-full border-t border-slate-100 px-4 py-3 text-left text-sm font-semibold text-primary hover:bg-primary/5"
                                onClick={() => {
                                  void (async () => {
                                    try {
                                      const created = await createDeviceMake({
                                        name: makeSearch.trim(),
                                        category: 'phone',
                                      });
                                      await queryClient.invalidateQueries({
                                        queryKey: ['device-makes'],
                                      });
                                      updateDevice(index, {
                                        makeId: created.id,
                                        makeName: created.name,
                                        modelId: '',
                                        modelName: '',
                                        modelSearch: '',
                                      });
                                      setMakeSearch('');
                                      toastSuccess(`${created.name} added to catalog`);
                                    } catch (err) {
                                      toastError(parseApiError(err).message);
                                    }
                                  })();
                                }}
                              >
                                ＋ Add &quot;{makeSearch.trim()}&quot; as manufacturer
                              </button>
                            ) : (
                              <div className="border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
                                Not in catalog — ask a manager to add it
                              </div>
                            )
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    <div className="relative mt-4">
                      <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
                      <input
                        className="w-full rounded-xl border border-slate-300 py-2 pl-10 pr-3"
                        placeholder="Search model..."
                        value={device.modelSearch || device.modelName}
                        onFocus={() => setActiveDevice(index)}
                        onChange={(e) => {
                          setActiveDevice(index);
                          updateDevice(index, { modelSearch: e.target.value, modelId: '', modelName: '' });
                        }}
                      />
                      {device.modelSearch && !device.modelId ? (
                        <div className="absolute z-20 mt-2 max-h-56 w-full overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg">
                          {models.slice(0, 12).map((model) => (
                            <button
                              key={model.id}
                              type="button"
                              className="block w-full px-4 py-3 text-left hover:bg-slate-50"
                              onClick={() =>
                                updateDevice(index, {
                                  modelId: model.id,
                                  modelName: model.name,
                                  modelSearch: model.name,
                                })
                              }
                            >
                              {model.name}
                            </button>
                          ))}
                          {models.length === 0 ||
                          !models.some(
                            (m) =>
                              m.name.toLowerCase() === device.modelSearch.trim().toLowerCase(),
                          ) ? (
                            <div className="border-t border-slate-100">
                              {models.length === 0 ? (
                                <div className="px-4 py-2 text-sm text-slate-500">
                                  No matching models
                                </div>
                              ) : null}
                              {device.makeId && device.modelSearch.trim() ? (
                                canQuickAddDevice ? (
                                  <button
                                    type="button"
                                    className="block w-full px-4 py-3 text-left text-sm font-semibold text-primary hover:bg-primary/5"
                                    onClick={() => {
                                      void (async () => {
                                        try {
                                          const make = makesQuery.data?.find(
                                            (m) => m.id === device.makeId,
                                          );
                                          const created = await createDeviceModel({
                                            makeId: device.makeId,
                                            name: device.modelSearch.trim(),
                                            category:
                                              (make?.category as
                                                | 'phone'
                                                | 'tablet'
                                                | 'laptop'
                                                | 'desktop'
                                                | 'console'
                                                | 'wearable'
                                                | 'other') || 'phone',
                                          });
                                          await queryClient.invalidateQueries({
                                            queryKey: ['device-models'],
                                          });
                                          updateDevice(index, {
                                            modelId: created.id,
                                            modelName: created.name,
                                            modelSearch: created.name,
                                          });
                                          toastSuccess(`${created.name} added to catalog`);
                                        } catch (err) {
                                          toastError(parseApiError(err).message);
                                        }
                                      })();
                                    }}
                                  >
                                    ＋ Add &quot;{device.modelSearch.trim()}&quot; to{' '}
                                    {device.makeName || 'catalog'}
                                  </button>
                                ) : (
                                  <div className="px-4 py-3 text-sm text-slate-500">
                                    Not in catalog — ask a manager to add it
                                  </div>
                                )
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    <div className="mt-4 grid gap-3 sm:grid-cols-3">
                      <input
                        className="rounded-xl border border-slate-300 px-3 py-2"
                        placeholder="IMEI"
                        value={device.imei}
                        onChange={(e) => updateDevice(index, { imei: e.target.value.replace(/\D/g, '').slice(0, 15) })}
                      />
                      <input
                        className="rounded-xl border border-slate-300 px-3 py-2"
                        placeholder="Color"
                        value={device.color}
                        onChange={(e) => updateDevice(index, { color: e.target.value })}
                      />
                      <input
                        className="rounded-xl border border-slate-300 px-3 py-2"
                        placeholder="Serial"
                        value={device.serialNumber}
                        onChange={(e) => updateDevice(index, { serialNumber: e.target.value })}
                      />
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {conditions.map(([condition, color]) => (
                        <button
                          key={condition}
                          type="button"
                          onClick={() => updateDevice(index, { condition })}
                          className={`rounded-xl px-3 py-2 text-sm font-semibold capitalize ${device.condition === condition ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}
                        >
                          <span className={`mr-2 inline-block h-2 w-2 rounded-full ${color}`} />
                          {condition}
                        </button>
                      ))}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {passcodeTypes.map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => updateDevice(index, { passcodeType: type, passcode: '' })}
                          className={`rounded-xl px-3 py-2 text-sm font-semibold capitalize ${device.passcodeType === type ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600'}`}
                        >
                          {type.replace(/_/g, ' ')}
                        </button>
                      ))}
                    </div>
                    {['pin', 'password', 'fingerprint'].includes(device.passcodeType) ? (
                      <input
                        className="mt-4 w-full rounded-xl border border-slate-300 px-3 py-2"
                        type="text"
                        autoComplete="off"
                        placeholder="Unlock Code"
                        value={device.passcode}
                        onChange={(e) => updateDevice(index, { passcode: e.target.value })}
                      />
                    ) : null}
                  </div>
                ))}
                <button
                  type="button"
                  className="rounded-xl border border-slate-300 px-4 py-2 font-semibold"
                  onClick={() => {
                    setDevices((d) => [...d, emptyDevice()]);
                    setActiveDevice(devices.length);
                    setShowManualDevice(false);
                    setHistoryDisplayName(null);
                  }}
                >
                  <Plus className="mr-2 inline h-4 w-4" />
                  Add Another Device
                </button>
              </>
            ) : null}
          </section>
        ) : null}

        {step === 3 ? (
          <section className="space-y-5">
            <h2 className="text-xl font-semibold text-slate-950">Step 3 - Issue / Repairs</h2>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Customer complaint</span>
              <SnippetTextarea
                className="mt-1 min-h-20 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary"
                value={customerComplaint}
                maxLength={500}
                onChange={(value) => setCustomerComplaint(value.slice(0, 500))}
                placeholder="What the customer said — e.g. dropped in water, screen flickers…"
              />
              <span className="mt-1 block text-xs text-slate-500">
                {customerComplaint.length}/500 · shown on the ticket and customer portal
              </span>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Internal diagnostic notes</span>
              <SnippetTextarea
                className="mt-1 min-h-16 w-full rounded-xl border border-amber-200 bg-amber-50/50 px-3 py-2 outline-none focus:border-amber-400"
                value={intakeNotes}
                onChange={setIntakeNotes}
                placeholder="Staff observations — not shown to the customer"
              />
            </label>
            <div className="flex flex-wrap gap-2">
              {devices.map((device, index) => (
                <button key={index} type="button" onClick={() => setActiveDevice(index)} className={`rounded-xl px-3 py-2 text-sm font-semibold ${activeDevice === index ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600'}`}>
                  {device.modelName || device.modelSearch || `Device ${index + 1}`}
                </button>
              ))}
            </div>
            {!currentDevice.modelId ? (
              <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Select a device model in Step 2 to see available repair services and prices.
              </p>
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {servicesQuery.data?.map((service) => {
                const selected = repairs[activeDevice]?.[service.id];
                return (
                  <div key={service.id} className={`rounded-2xl border p-4 ${selected ? 'border-primary bg-primary/5' : 'border-slate-200'}`}>
                    <button type="button" className="w-full text-left" onClick={() => toggleService(service)}>
                      <div className="font-semibold">{service.name}</div>
                      <div className="mt-2 flex justify-between text-sm">
                        <span>{formatCurrency(servicePrice(service))}</span>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">{service.hasPriceOverride ? 'Device price' : 'Default'}</span>
                      </div>
                    </button>
                    {selected ? (
                      <input
                        className="mt-3 w-full rounded-xl border border-slate-300 px-3 py-2"
                        type="number"
                        min="0"
                        step="0.01"
                        value={selected.price}
                        onChange={(e) =>
                          setRepairs((current) => ({
                            ...current,
                            [activeDevice]: {
                              ...(current[activeDevice] ?? {}),
                              [service.id]: { service, price: e.target.value },
                            },
                          }))
                        }
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
            <div className="rounded-2xl border border-dashed border-slate-300 p-4">
              <div className="mb-3 font-semibold">Custom Repair</div>
              <div className="grid gap-3 sm:grid-cols-[1fr_120px_100px_auto]">
                <input
                  className="rounded-xl border border-slate-300 px-3 py-2"
                  value={customRepairName}
                  onChange={(e) => setCustomRepairName(e.target.value)}
                  placeholder="Repair name"
                />
                <input
                  className="rounded-xl border border-slate-300 px-3 py-2"
                  type="number"
                  min="0"
                  step="0.01"
                  value={customRepairPrice}
                  onChange={(e) => setCustomRepairPrice(e.target.value)}
                  placeholder="Price"
                />
                <input
                  className="rounded-xl border border-slate-300 px-3 py-2"
                  type="number"
                  min="0"
                  value={customRepairMinutes}
                  onChange={(e) => setCustomRepairMinutes(e.target.value)}
                  placeholder="Mins"
                  title="Estimated minutes"
                />
                <button
                  type="button"
                  className="rounded-xl bg-primary px-4 py-2 font-semibold text-white"
                  onClick={() => {
                    if (!customRepairName.trim()) return;
                    const id = editingCustomId ?? `custom-${Date.now()}`;
                    const service = {
                      id,
                      name: customRepairName.trim(),
                      defaultPrice: customRepairPrice,
                      estimatedMinutes: Number(customRepairMinutes) || null,
                    } as RepairService;
                    setRepairs((current) => ({
                      ...current,
                      [activeDevice]: {
                        ...(current[activeDevice] ?? {}),
                        [id]: { service, price: customRepairPrice },
                      },
                    }));
                    setCustomRepairName('');
                    setCustomRepairPrice('0');
                    setCustomRepairMinutes('60');
                    setEditingCustomId(null);
                  }}
                >
                  {editingCustomId ? 'Save' : 'Add'}
                </button>
              </div>
              {Object.entries(repairs[activeDevice] ?? {})
                .filter(([id]) => id.startsWith('custom-'))
                .map(([id, row]) => (
                  <div
                    key={id}
                    className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                  >
                    <div>
                      <span className="font-semibold text-slate-900">{row.service.name}</span>
                      <span className="ml-2 text-slate-600">{formatCurrency(row.price)}</span>
                      {row.service.estimatedMinutes != null ? (
                        <span className="ml-2 text-slate-500">
                          · {String(row.service.estimatedMinutes)} min
                        </span>
                      ) : null}
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="text-xs font-semibold text-primary"
                        onClick={() => {
                          setEditingCustomId(id);
                          setCustomRepairName(row.service.name);
                          setCustomRepairPrice(row.price);
                          setCustomRepairMinutes(
                            String(
                              (row.service as RepairService & { estimatedMinutes?: number })
                                .estimatedMinutes ?? 60,
                            ),
                          );
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="text-xs font-semibold text-red-600"
                        onClick={() => {
                          setRepairs((current) => {
                            const next = { ...(current[activeDevice] ?? {}) };
                            delete next[id];
                            return { ...current, [activeDevice]: next };
                          });
                          if (editingCustomId === id) {
                            setEditingCustomId(null);
                            setCustomRepairName('');
                          }
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </section>
        ) : null}

        {step === 4 ? (
          <form className="space-y-5" onSubmit={(event) => { event.preventDefault(); createNow(false); }}>
            <h2 className="text-xl font-semibold text-slate-950">Step 4 - Confirm & Create</h2>
            {templateApplied ? (
              <button
                type="button"
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold"
                onClick={clearAppliedTemplate}
              >
                ← Change template
              </button>
            ) : null}
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="font-semibold">Summary</div>
              <div className="mt-2 text-sm text-slate-600">{devices.length} device(s), {selectedRepairs.length} repair(s)</div>
              {selectedRepairs.map((repair, index) => (
                <div key={`${repair.serviceName}-${index}`} className="mt-2 flex justify-between">
                  <span>{repair.serviceName}</span>
                  <span className="font-semibold">{formatCurrency(repair.quotedPrice)}</span>
                </div>
              ))}
              <div className="mt-3 flex justify-between border-t border-slate-200 pt-3 text-lg font-bold">
                <span>Total</span>
                <span>{formatCurrency(total)}</span>
              </div>
            </div>
            {customerComplaint.trim() ? (
              <div className="rounded-2xl border border-primary/20 bg-primary/[0.04] p-4">
                <div className="text-xs font-bold uppercase tracking-wide text-primary">Customer complaint</div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-800">{customerComplaint.trim()}</p>
              </div>
            ) : null}
            {intakeNotes.trim() ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <div className="text-xs font-bold uppercase tracking-wide text-amber-800">Internal diagnostic notes</div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-amber-950">{intakeNotes.trim()}</p>
              </div>
            ) : null}

            {depositPreview?.showSection ? (
              <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4 space-y-3">
                {depositPreview.depositRequired ? (
                  <p className="font-semibold text-amber-950">⚠ Deposit required</p>
                ) : (
                  <p className="font-semibold text-slate-900">Collect a deposit? (optional)</p>
                )}
                {depositPreview.usePercentage ? (
                  <p className="text-sm text-slate-600">
                    {depositPreview.percentage}% = {formatCurrency(depositPreview.depositAmount)}
                  </p>
                ) : null}
                {!depositPreview.depositRequired ? (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className={[
                        'rounded-xl px-4 py-2 text-sm font-semibold',
                        collectDepositChoice === 'skip' ? 'bg-slate-800 text-white' : 'bg-white border',
                      ].join(' ')}
                      onClick={() => setCollectDepositChoice('skip')}
                    >
                      No Deposit
                    </button>
                    <button
                      type="button"
                      className={[
                        'rounded-xl px-4 py-2 text-sm font-semibold',
                        collectDepositChoice === 'collect' ? 'bg-primary text-white' : 'bg-white border',
                      ].join(' ')}
                      onClick={() => setCollectDepositChoice('collect')}
                    >
                      Collect Deposit
                    </button>
                  </div>
                ) : null}
        {(depositPreview.depositRequired || collectDepositChoice === 'collect') ? (
                  <label className="block text-sm">
                    <span className="font-medium">
                      {depositPreview.depositRequired ? 'Deposit amount' : 'Deposit amount to collect'}
                    </span>
                    <input
                      type="number"
                      min={0.01}
                      step="0.01"
                      className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                      value={depositAmountInput}
                      onChange={(e) => setDepositAmountInput(e.target.value)}
                      readOnly={
                        depositPreview.depositRequired && !canManageStoreRole(user?.role)
                      }
                    />
                    <span className="mt-1 block text-xs text-slate-600">
                      Payment method is chosen in the next step.
                    </span>
                  </label>
                ) : null}
                {depositPreview.depositRequired && canWaiveDeposit ? (
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={managerSkipDeposit}
                      onChange={(e) => setManagerSkipDeposit(e.target.checked)}
                    />
                    Skip deposit (requires manager PIN)
                  </label>
                ) : null}
                <p className="text-xs text-slate-600">
                  Deposit is collected right after the ticket is created.
                </p>
              </section>
            ) : null}

            <PromisedTimePanel
              serviceIds={selectedRepairs
                .map((r) => r.serviceId)
                .filter((id): id is string => Boolean(id))}
              value={promisedDate}
              onChange={setPromisedDate}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <select className="rounded-xl border border-slate-300 px-3 py-2 capitalize" value={priority} onChange={(e) => setPriority(e.target.value as typeof priority)}>
                <option value="normal">Normal</option>
                <option value="rush">Rush</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="flex flex-wrap justify-end gap-3">
              <button type="button" className="rounded-xl bg-accent px-4 py-2 font-semibold text-white" onClick={() => createNow(true)}>
                <Printer className="mr-2 inline h-4 w-4" />
                Create & Print Label
              </button>
              <button className="rounded-xl bg-primary px-4 py-2 font-semibold text-white" disabled={createMutation.isPending}>
                Create Ticket
              </button>
            </div>
          </form>
        ) : null}

        <div className="mt-6 flex justify-between border-t border-slate-200 pt-5">
          <button className="rounded-xl border border-slate-300 px-4 py-2 font-semibold disabled:opacity-40" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>Back</button>
          {step > 0 && step < 4 ? <button className="rounded-xl bg-primary px-4 py-2 font-semibold text-white" onClick={() => setStep((s) => Math.min(4, s + 1))}>Continue</button> : null}
        </div>
      </div>
      {depositModalTicket ? (
        <CollectDepositModal
          open
          variant={depositModalTicket.mandatoryIntake ? 'mandatory_intake' : 'default'}
          showManagerOverride={
            depositModalTicket.mandatoryIntake && canWaiveDeposit
          }
          successToastMessage={
            depositModalTicket.mandatoryIntake ? 'Deposit collected — ticket created ✓' : undefined
          }
          onManagerSkip={
            depositModalTicket.mandatoryIntake && canWaiveDeposit
              ? async () => {
                  const result = await requireOverride({
                    action: 'waive_deposit',
                    title: 'Manager Approval to Waive Deposit',
                    description: `Waiving required deposit of ${formatCurrency(depositModalTicket.maxAmount)}`,
                    context: {
                      ticketId: depositModalTicket.id,
                      amount: depositModalTicket.maxAmount,
                    },
                  });
                  if (!result.approved) return false;
                  await updateTicket(depositModalTicket.id, {
                    depositSkipped: true,
                    managerOverrideAuthorizedBy: result.authorizedBy.id,
                  });
                  toastInfo('Deposit skipped — manager override');
                  void queryClient.invalidateQueries({ queryKey: ['ticket'] });
                  void queryClient.invalidateQueries({ queryKey: ['tickets'] });
                  const pending = pendingAfterDeposit;
                  setDepositModalTicket(null);
                  setPendingAfterDeposit(null);
                  if (pending) {
                    offerSaveTemplateAfterCreate();
                    finishAfterCreate(pending.ticketId, pending.print);
                  }
                  return true;
                }
              : undefined
          }
          ticketId={depositModalTicket.id}
          ticketNumber={depositModalTicket.ticketNumber}
          maxAmount={depositModalTicket.maxAmount}
          customerHasStoreCredit={
            Number(
              selectedCustomer?.storeCreditBalance ??
                existingCustomerQuery.data?.storeCreditBalance ??
                0,
            ) > 0
          }
          onClose={() => {
            setDepositModalTicket(null);
            if (pendingAfterDeposit) {
              finishAfterCreate(pendingAfterDeposit.ticketId, pendingAfterDeposit.print);
              setPendingAfterDeposit(null);
            }
          }}
          onSuccess={() => {
            const wasMandatory = depositModalTicket.mandatoryIntake;
            const ticketNum = depositModalTicket.ticketNumber;
            void queryClient.invalidateQueries({ queryKey: ['ticket'] });
            void queryClient.invalidateQueries({ queryKey: ['tickets'] });
            const pending = pendingAfterDeposit;
            setDepositModalTicket(null);
            setPendingAfterDeposit(null);
            if (!wasMandatory) {
              toastSuccess(`Ticket ${ticketNum} created successfully`);
            }
            if (pending) {
              offerSaveTemplateAfterCreate();
              finishAfterCreate(pending.ticketId, pending.print);
            }
          }}
        />
      ) : null}
      {managerOverrideModal ? <ManagerOverrideModal {...managerOverrideModal} /> : null}
    </div>
  );
}
